import { makeRef } from "@/components/composables/reactivityTypes";
import type { Maybe } from "@/components/utils/typeHelpers";
import type { IControl } from "@/controls/control";
import type { PartialQ } from "@/controls/tpcontrol";
import {
  controlDefaults,
  extendDefaults,
  TpControl,
} from "@/controls/tpcontrol";
import { type IPadding } from "@tp/tpcontrols/paddings";
import { styleFormat } from "@tp/tpcontrols/styleFormat";
import { computed, type CreateElement } from "vue";

export const containerControlDefaults = extendDefaults(controlDefaults, {
  backColour: "",
  innerPad: null as IPadding | null,
});

export type ContainerControlIn = typeof containerControlDefaults;

abstract class ContainerControl<
  TIn extends ContainerControlIn,
> extends TpControl<TIn> {
  constructor(defaults: TIn, ...opts: PartialQ<TIn>[]) {
    super(defaults, ...opts);
  }

  private children = makeRef<readonly IControl[]>([]);

  protected visibleChildren = computed(() =>
    this.children.value.filter((x) => x.in.visible)
  );

  protected _setChildren(newChildSet: readonly IControl[]) {
    const newSet = new Set<IControl>(newChildSet);
    for (const removedChild of this.children.value.filter(
      (x) => !newSet.has(x)
    )) {
      removedChild.parent = null;
    }
    for (const child of newChildSet.filter((x) => x.parent !== this)) {
      child.parent = this;
    }

    this.children.value = newChildSet;
  }

  renderVisibleChildren(h: CreateElement) {
    return this.visibleChildren.value.map((x) => x.render(h));
  }
}

export abstract class ContainerSingle<
  TIn extends ContainerControlIn,
> extends ContainerControl<TIn> {
  setChild(newChild: Maybe<IControl>) {
    this._setChildren(newChild ? [newChild] : []);
  }
}

export abstract class ContainerMultiple<
  TIn extends ContainerControlIn,
> extends ContainerControl<TIn> {
  setChildren(newChildSet: readonly IControl[]) {
    this._setChildren(newChildSet);
  }
}

const vStackDefaults = extendDefaults(containerControlDefaults, {
  spaceBetween: 0,
});

type VStackIn = typeof vStackDefaults;

export class VStack extends ContainerMultiple<VStackIn> {
  constructor(...opts: PartialQ<VStackIn>[]) {
    super(vStackDefaults, ...opts);
  }

  render(h: CreateElement) {
    const rows = this.visibleChildren.value.map((child, index) =>
      h(
        "div",
        index === 0 || this.in.spaceBetween === 0
          ? {}
          : { style: "margin-top: " + this.in.spaceBetween + "px; " },
        [child.render(h)]
      )
    );

    return h(
      "div",
      {
        style:
          "width: 100%; " +
          styleFormat.fromPadding(this.in.innerPad) +
          styleFormat.fromBackColour(this.in.backColour),
      },
      rows
    );
  }
}

const vSpaceDefaults = extendDefaults(controlDefaults, {
  height: 0,
});

type VSpaceIn = typeof vSpaceDefaults;

export class VSpace extends TpControl<VSpaceIn> {
  constructor(...opts: PartialQ<VSpaceIn>[]) {
    super(vSpaceDefaults, ...opts);
  }

  render(h: CreateElement) {
    return h("div", {
      style: "height: " + this.in.height + "px; ",
    });
  }
}

const centeredDefaults = extendDefaults(containerControlDefaults, {
  maxWidth: null as number | null,
});

type CenteredIn = typeof centeredDefaults;

class Centered extends ContainerSingle<CenteredIn> {
  constructor(...opts: PartialQ<CenteredIn>[]) {
    super(centeredDefaults, ...opts);
  }

  render(h: CreateElement) {
    return h(
      "div",
      {
        style:
          "display: flex; justify-content: center;" +
          styleFormat.fromPadding(this.in.innerPad) +
          styleFormat.fromBackColour(this.in.backColour),
      },
      [
        h(
          "span",
          {},
          this.visibleChildren.value.map((child) => child.render(h))
        ),
      ]
    );
  }
}

class CenteredDiv extends ContainerSingle<CenteredIn> {
  constructor(...opts: PartialQ<CenteredIn>[]) {
    super(centeredDefaults, ...opts);
  }

  render(h: CreateElement) {
    return h(
      "div",
      {
        style:
          "display: flex; justify-content: center; width: 100%; " +
          //styleFormat.fromMaxWidth(this.in.maxWidth) +
          styleFormat.fromPadding(this.in.innerPad) +
          styleFormat.fromBackColour(this.in.backColour),
      },
      [
        h(
          "div",
          {
            style: "width: 100%; " + styleFormat.fromMaxWidth(this.in.maxWidth),
          },
          this.visibleChildren.value.map((child) => child.render(h))
        ),
      ]
    );
  }
}

const leftCenterRightDefaults = extendDefaults(controlDefaults, {
  innerPad: null as IPadding | null,
  gap: 0,
});
type LeftCenterRightIn = typeof leftCenterRightDefaults;

class LeftCenterRight extends TpControl<LeftCenterRightIn> {
  private _left = makeRef<IControl | null>(null);
  private _center = makeRef<IControl | null>(null);
  private _right = makeRef<IControl | null>(null);

  constructor(...opts: PartialQ<LeftCenterRightIn>[]) {
    super(leftCenterRightDefaults, ...opts);
  }

  private updateChildsParent(
    oldChild: Maybe<IControl>,
    newChild: Maybe<IControl>
  ) {
    if (oldChild !== newChild) {
      if (oldChild) {
        oldChild.parent = null;
      }
      if (newChild) {
        newChild.parent = this;
      }
    }
  }

  public setLeft(control: Maybe<IControl>) {
    this.updateChildsParent(this._left.value, control);
    this._left.value = control ?? null;
  }

  public setCenter(control: Maybe<IControl>) {
    this.updateChildsParent(this._center.value, control);
    this._center.value = control ?? null;
  }

  public setRight(control: Maybe<IControl>) {
    this.updateChildsParent(this._right.value, control);
    this._right.value = control ?? null;
  }

  private renderedControl(control: IControl | null, h: CreateElement) {
    return control && control.in.visible ? [control.render(h)] : [];
  }

  render(h: CreateElement) {
    return h(
      "div",
      {
        style: "display: flex; " + styleFormat.fromPadding(this.in.innerPad),
      },
      [
        h("span", {}, this.renderedControl(this._left.value, h)),
        h(
          "span",
          {
            style:
              "flex: 1; display: flex; justify-content: center; padding-left: " +
              this.in.gap +
              "px; padding-right: " +
              this.in.gap +
              "px",
          },
          this.renderedControl(this._center.value, h)
        ),
        h("span", {}, this.renderedControl(this._right.value, h)),
      ]
    );
  }
}

const hStackDefaults = extendDefaults(containerControlDefaults, {
  // None : No handling of spare space
  // GrowFirst : Grow the first child to take up spare space
  // GrowLast : Grow the last child to take up spare space
  // GrowAll : Grow all children equally to take up spare space
  // Spread : Spread the children out to use up spare space
  spareSpace: "none" as
    | "none"
    | "growfirst"
    | "growsecond"
    | "growlast"
    | "growall"
    | "spread",
  gap: 0,
});

type HStackIn = typeof hStackDefaults;

class HStack extends ContainerMultiple<HStackIn> {
  constructor(...opts: PartialQ<HStackIn>[]) {
    super(hStackDefaults, ...opts);
  }

  render(h: CreateElement) {
    const spare = this.in.spareSpace;
    const visibleChildren = this.visibleChildren.value;

    const childNodes = visibleChildren.map((child, index) => {
      const grows =
        spare === "growall" ||
        (spare === "growfirst" && index === 0) ||
        (spare === "growsecond" && index === 1) ||
        (spare === "growlast" && index === visibleChildren.length - 1);

      return h(
        "span",
        {
          style: grows ? "flex: 1;" : "",
        },
        [child.render(h)]
      );
    });

    return h(
      "div",
      {
        style:
          "width: 100%; display: flex; flex-flow: row wrap; justify-content:" +
          (this.in.spareSpace === "spread"
            ? "space-between; "
            : "flex-start; ") +
          "gap: " +
          this.in.gap +
          "px; " +
          styleFormat.fromPadding(this.in.innerPad) +
          styleFormat.fromBackColour(this.in.backColour),
      },

      childNodes
    );
  }
}

const hSpanDefaults = extendDefaults(containerControlDefaults, {
  gap: 0,
});

type HSpanIn = typeof hSpanDefaults;

class HSpan extends ContainerMultiple<HSpanIn> {
  constructor(...opts: PartialQ<HSpanIn>[]) {
    super(hSpanDefaults, ...opts);
  }

  render(h: CreateElement) {
    const visibleChildren = this.visibleChildren.value;

    const childNodes = visibleChildren.map((child, index) =>
      h(
        "span",
        {
          style:
            "display: inline-block; " +
            (this.in.gap > 0 && index !== visibleChildren.length - 1
              ? "padding-right: " + this.in.gap + "px; "
              : ""),
        },
        [child.render(h)]
      )
    );

    return h(
      "span",
      {
        style:
          //"width: max-content;" +
          styleFormat.fromPadding(this.in.innerPad) +
          styleFormat.fromBackColour(this.in.backColour),
      },
      childNodes
    );
  }
}

const spanDefaults = extendDefaults(containerControlDefaults, {
  gap: 0,
});

type SpanIn = typeof spanDefaults;

class Span extends ContainerMultiple<SpanIn> {
  constructor(...opts: PartialQ<SpanIn>[]) {
    super(spanDefaults, ...opts);
  }

  render(h: CreateElement) {
    const visibleChildren = this.visibleChildren.value;

    const childNodes = visibleChildren.map((child, index) =>
      this.in.gap === 0
        ? child.render(h)
        : h("span", { style: "padding-left: " + this.in.gap + "px; " }, [
            child.render(h),
          ])
    );

    return h(
      "span",
      {
        style:
          styleFormat.fromPadding(this.in.innerPad) +
          styleFormat.fromBackColour(this.in.backColour),
      },
      childNodes
    );
  }
}

const leftRightDefaults = extendDefaults(controlDefaults, {
  innerPad: null as IPadding | null,
});
type LeftRightIn = typeof leftRightDefaults;

export class LeftRight extends TpControl<LeftRightIn> {
  private _left = makeRef<IControl | null>(null);
  private _right = makeRef<IControl | null>(null);

  constructor(...opts: PartialQ<LeftRightIn>[]) {
    super(leftRightDefaults, ...opts);
  }

  private updateChildsParent(
    oldChild: Maybe<IControl>,
    newChild: Maybe<IControl>
  ) {
    if (oldChild !== newChild) {
      if (oldChild) {
        oldChild.parent = null;
      }
      if (newChild) {
        newChild.parent = this;
      }
    }
  }

  public setLeft(control: Maybe<IControl>) {
    this.updateChildsParent(this._left.value, control);
    this._left.value = control ?? null;
  }

  public setRight(control: Maybe<IControl>) {
    this.updateChildsParent(this._right.value, control);
    this._right.value = control ?? null;
  }

  render(h: CreateElement) {
    const left = this._left.value;
    const right = this._right.value;
    return h(
      "div",
      {
        style:
          "width: 100%; display: flex; flex-flow: row wrap; justify-content: space-between; " +
          styleFormat.fromPadding(this.in.innerPad),
      },
      [
        left && left.in.visible ? left.render(h) : h("span"),
        right && right.in.visible ? right.render(h) : h("span"),
      ]
    );
  }
}

const tpLayouts = {
  vStack: (opts?: Partial<VStackIn>, contents?: readonly IControl[]) => {
    const vstack = new VStack(opts);
    vstack.setChildren(contents ?? []);
    return vstack;
  },
  centered: (opts?: Partial<CenteredIn>, contents?: IControl) => {
    const centered = new Centered(opts);
    centered.setChild(contents);
    return centered;
  },
  centeredDiv: (opts?: Partial<CenteredIn>, contents?: IControl) => {
    const centered = new CenteredDiv(opts);
    centered.setChild(contents);
    return centered;
  },
  leftCenterRight: (
    opts?: Partial<LeftCenterRightIn>,
    left?: IControl | null,
    center?: IControl | null,
    right?: IControl | null
  ) => {
    const control = new LeftCenterRight(opts);
    control.setLeft(left);
    control.setCenter(center);
    control.setRight(right);
    return control;
  },
  hStack: (opts?: Partial<HStackIn>, contents?: readonly IControl[]) => {
    const hstack = new HStack(opts);
    hstack.setChildren(contents ?? []);
    return hstack;
  },
  hSpan: (opts?: Partial<HSpanIn>, contents?: readonly IControl[]) => {
    const ctrl = new HSpan(opts);
    ctrl.setChildren(contents ?? []);
    return ctrl;
  },
  span: (opts?: Partial<SpanIn>, contents?: readonly IControl[]) => {
    const ctrl = new Span(opts);
    ctrl.setChildren(contents ?? []);
    return ctrl;
  },
  leftRight: (
    opts?: Partial<LeftRightIn>,
    left?: IControl | null,
    right?: IControl | null
  ) => {
    const control = new LeftRight(opts);
    control.setLeft(left);
    control.setRight(right);
    return control;
  },
  vSpace: (opts?: Partial<VSpaceIn>) => new VSpace(opts),
};

export { tpLayouts };
